import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useUser } from '/imports/hooks';
import { Account, Roles } from '/imports/api/accounts';
import { Icon } from '/imports/ui/Icon';
import { buildNavEntries, navDestinations, NavDestination } from '/imports/ui/navItems';

type Hit = NavDestination & { section: string; score: number };

/**
 * Subsequence match, the standard command-palette behaviour: "btr" finds
 * "Buy Targets". Scoring prefers, in order, a prefix match on the label, a
 * match on the label over one on a hidden keyword, and tighter spans over
 * scattered ones, so exact typing always beats an incidental subsequence.
 * Returns null when the query does not match at all.
 */
const score = (item: NavDestination & { section: string }, query: string): number | null => {
  const q = query.toLowerCase();
  if (!q) return 0;
  const label = item.text.toLowerCase();
  if (label.startsWith(q)) return 1000 - label.length;
  const haystacks = [label, ...(item.keywords || []).map((k) => k.toLowerCase())];
  let best: number | null = null;
  for (let h = 0; h < haystacks.length; h++) {
    const hay = haystacks[h];
    const direct = hay.indexOf(q);
    if (direct >= 0) {
      const value = 700 - direct - (h > 0 ? 200 : 0);
      if (best === null || value > best) best = value;
      continue;
    }
    // Subsequence: walk the query through the haystack, tracking the span used.
    let qi = 0, first = -1, last = -1;
    for (let i = 0; i < hay.length && qi < q.length; i++) {
      if (hay[i] !== q[qi]) continue;
      if (first < 0) first = i;
      last = i;
      qi++;
    }
    if (qi < q.length) continue;
    const span = last - first + 1;
    const value = 400 - span - first - (h > 0 ? 200 : 0);
    if (best === null || value > best) best = value;
  }
  return best;
};

/**
 * Cmd-K / Ctrl-K palette.
 *
 * Destinations come from the same builder the sidebar uses, so the two cannot
 * disagree about what exists or who may see it.
 */
export const CommandPalette = ({ account }: { account?: Account }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const navigate = useNavigate();
  const user = useUser();
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const destinations = useMemo(() => navDestinations(buildNavEntries({
    isSuperUser: !!user?.isSuperUser,
    isAdminOrSuper: !!user?.isSuperUser || !!account?.roles?.includes(Roles.admin),
  })), [user, account]);

  const hits = useMemo<Hit[]>(() => {
    const scored: Hit[] = [];
    for (const d of destinations) {
      const s = score(d, query.trim());
      if (s !== null) scored.push({ ...d, score: s });
    }
    // Stable within equal scores: keep sidebar order so an empty query reads as
    // the sidebar rather than an arbitrary shuffle.
    return query.trim() ? scored.sort((a, b) => b.score - a.score) : scored;
  }, [destinations, query]);

  const close = useCallback(() => {
    setOpen(false);
    setQuery('');
    setActive(0);
  }, []);

  const go = useCallback((path: string) => {
    close();
    navigate(path);
  }, [close, navigate]);

  // Global hotkey. Bound on keydown at the document so it works wherever focus
  // is; the palette's own keys are handled on the input instead of here so an
  // Escape inside a different overlay does not close this one by accident.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        // Reset on the way out too, so reopening never shows the previous query.
        setOpen((wasOpen) => {
          if (wasOpen) { setQuery(''); setActive(0); }
          return !wasOpen;
        });
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  // Clamp rather than reset: retyping should not silently jump the selection
  // back to the top when the old index is still valid.
  useEffect(() => {
    setActive((i) => (i >= hits.length ? Math.max(0, hits.length - 1) : i));
  }, [hits.length]);

  useEffect(() => {
    if (!open) return;
    listRef.current?.querySelector('[data-active="true"]')?.scrollIntoView({ block: 'nearest' });
  }, [active, open]);

  if (!open) return null;

  const onInputKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') { e.preventDefault(); close(); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => (hits.length ? (i + 1) % hits.length : 0)); return; }
    if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => (hits.length ? (i - 1 + hits.length) % hits.length : 0)); return; }
    if (e.key === 'Enter') {
      e.preventDefault();
      const hit = hits[active];
      if (hit) go(hit.path);
    }
  };

  let lastSection = '';
  return createPortal(
    <div
      className="command-palette fixed top-0 left-0 z-100 flex h-full w-full flex-col items-center overflow-auto bg-overlay px-4 py-[12vh]"
      onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}
    >
      <div className="palette-window flex max-h-[70vh] w-full max-w-[560px] flex-col overflow-hidden rounded-card border border-soft bg-panel shadow-chunky-lg">
        <div className="flex shrink-0 items-center gap-2 border-b border-soft px-3">
          <Icon icon="search" size={16} className="text-fg-3" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => { setQuery(e.target.value); setActive(0); }}
            onKeyDown={onInputKey}
            placeholder="Jump to..."
            aria-label="Jump to a page"
            className="h-11 w-full border-none bg-transparent text-[14px] text-fg-1 placeholder:text-fg-3 focus:outline-none"
          />
          <kbd className="shrink-0 rounded border border-soft bg-page-alt px-1.5 py-0.5 font-mono text-[11px] text-fg-3">esc</kbd>
        </div>
        <div ref={listRef} className="min-h-0 flex-1 overflow-auto py-1">
          {hits.length === 0 && (
            <div className="px-4 py-6 text-center text-[13px] text-fg-3">No matches</div>
          )}
          {hits.map((hit, i) => {
            const header = !query.trim() && hit.section !== lastSection ? hit.section : null;
            if (header) lastSection = hit.section;
            return (
              <React.Fragment key={hit.id}>
                {header && <div className="eyebrow px-3 pt-3 pb-1 text-fg-3">{header}</div>}
                <div
                  data-active={i === active}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => go(hit.path)}
                  className={`mx-1 flex h-9 cursor-pointer items-center gap-2.5 rounded-md px-2.5 text-[13px] ${i === active ? 'bg-primary/10 text-primary' : 'text-fg-1'}`}
                >
                  {hit.icon && <Icon icon={hit.icon} size={16} />}
                  <span className="truncate">{hit.text}</span>
                  {query.trim() && hit.section && <span className="ml-auto shrink-0 text-[11px] text-fg-3">{hit.section}</span>}
                </div>
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>,
    document.body,
  );
};
